import { FC, memo } from 'react';
import { CgChevronDown, CgClose, CgMenu } from 'react-icons/cg';
import { Link } from 'react-router';
import {
  Box,
  Button,
  Collapsible,
  Flex,
  Icon,
  IconButton,
  Link as ChakraLink,
  Menu,
  Portal,
  Stack,
} from '@chakra-ui/react';

import { MiddleOfficeLogo } from '../../MiddleOfficeLogo';
import { useHeader } from './hooks/useHeader';
import { HeaderProps } from './types/HeaderProps';
import { DesktopNav } from './DesktopNav';
import { MobileNav } from './MobileNav';

const HeaderComponent: FC<HeaderProps> = () => {
  const {
    mobileNavCollapseApi,
    middleOfficeConfig,
    homeNavItem,
    navItems,
    user,
    logout,
  } = useHeader();

  return (
    <>
      <Flex
        alignItems="center"
        borderBottom={1}
        borderColor="gray.200"
        borderStyle="solid"
        color="gray.600"
        layerStyle="header"
        minH="60px"
        px={4}
      >
        <Flex
          display={{ base: 'flex', md: 'none' }}
          flex={{ base: 1, md: 'auto' }}
          ml={-2}
        >
          <IconButton
            aria-label="Toggle Navigation"
            onClick={mobileNavCollapseApi.onToggle}
            variant="ghost"
          >
            {mobileNavCollapseApi.open ? (
              <Icon as={CgClose} boxSize={3} />
            ) : (
              <Icon as={CgMenu} boxSize={5} />
            )}
          </IconButton>
        </Flex>

        <Flex
          alignItems="center"
          flex={1}
          justifyContent={{ base: 'center', md: 'start' }}
        >
          {homeNavItem && (
            <ChakraLink
              _focus={{ outline: 'none' }}
              _focusVisible={{ outline: 'none' }}
              asChild
            >
              <Link to={homeNavItem.href}>
                <MiddleOfficeLogo variant="light" />
              </Link>
            </ChakraLink>
          )}
          {!homeNavItem && <MiddleOfficeLogo variant="light" />}
          <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
            <DesktopNav navItems={navItems} />
          </Flex>
        </Flex>

        <Stack
          direction="row"
          flex={{ base: 1, md: 0 }}
          gap={6}
          justifyContent="flex-end"
        >
          <Box position="relative">
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  _expanded={{ background: 'inherit' }}
                  fontSize="md"
                  fontWeight={400}
                  {...middleOfficeConfig.getStyles('headerMenuButton')}
                >
                  {user?.name}
                  <CgChevronDown />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content layerStyle="headerMenuList" position="relative">
                    <Menu.Item onClick={logout} value="logout">
                      Logout
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>
        </Stack>
      </Flex>

      <Collapsible.Root open={mobileNavCollapseApi.open}>
        <Collapsible.Content>
          <MobileNav navItems={navItems} />
        </Collapsible.Content>
      </Collapsible.Root>
    </>
  );
};

export const Header = memo(HeaderComponent);
